import React, { FC, ReactNode, useCallback } from 'react';
import { SpocChangeDialog } from './SpocChangeDialog';
import { Redirect, useHistory, useLocation } from 'react-router';
import { PATH_CATALOGUE } from '../../../store/constants';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthenticatedSpocById } from '../../../store/authentication/actions';
import { AppState } from '../../../store/appState';
import { useRequiredSpocQuery } from './useRequiredSpocQuery';
import LoadingOverlay from '../../components/LoadingOverlay';
import { LoadingWrapper } from '../../components/LoadingWrapper';
import { PageNotFound } from '../../../shop/PageNotFound';
import { useQueryParams } from '../../hooks/useQueryParams';
import classNames from 'classnames';

export enum SpocRequiredFor {
    ORDER = 'order',
    ORDERLIST = 'orderlist',
    LOCAL_CONTENT_PRODUCT = 'localContentProduct'
}

type Props = {
    readonly type: SpocRequiredFor;
    readonly id: string;
    readonly children: ReactNode;
};

export const CheckRequiredSpoc: FC<Props> = ({ type, id, children }) => {
    const history = useHistory();
    const location = useLocation();
    const {
        spocId: { value: spocIdParam }
    } = useQueryParams({ spocId: 'string' });

    const dispatch = useDispatch();
    const authentication = useSelector((state: AppState) => state.authentication);

    const requiredSpoc = useRequiredSpocQuery(type, id);

    const acceptSpocChange = useCallback(() => {
        if (!requiredSpoc.data.spocId) {
            return;
        }

        dispatch(setAuthenticatedSpocById(requiredSpoc.data.spocId, location.pathname, '/catalogue'));
    }, [dispatch, requiredSpoc.data.spocId, location.pathname]);

    const rejectSpocChange = useCallback(() => {
        history.push(PATH_CATALOGUE);
    }, [history]);

    const isLoading = requiredSpoc.status === 'loading';

    if (isLoading) {
        return (
            <div className={classNames({ 'min-h-screen': isLoading })}>
                <LoadingWrapper>
                    <LoadingOverlay loading={true} />
                </LoadingWrapper>
            </div>
        );
    }

    if (requiredSpoc.status === 'error') {
        return <PageNotFound />;
    }

    const spocChangeRequired = authentication.spoc.id !== requiredSpoc.data.spocId;

    if (spocChangeRequired) {
        if (spocIdParam && requiredSpoc.data.spocId !== spocIdParam) {
            // In this case, the user just switched to another spoc and doesn't want to see the SpocChangeDialog.
            // Instead, we redirect to the root page, since the current page is not available for the current spoc.
            return <Redirect to="/" />;
        }

        return (
            <SpocChangeDialog
                componentName={type}
                spocName={requiredSpoc.data.spocName!}
                onAcceptSpocChange={acceptSpocChange}
                onRejectSpocChange={rejectSpocChange}
            />
        );
    }

    return <>{children}</>;
};
